import React, { useMemo, useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import BdrSelectPopup from "../../../../components/BdrInput/BdrSelectPopup";
import BdrCheckbox from "../../../../components/BdrCheckbox/BdrCheckbox";
import { GET_DOOR_COST_SNAPSHOT, GET_DOOR_COST_SNAPSHOTS } from "../../../../utils/GraphQL/queries";
import { COMPUTE_DOOR_COST_SNAPSHOT } from "../../../../utils/GraphQL/mutations";
import { useSnackbar } from "../../../../utils/GlobalSnackbar/GlobalSnackbarContext";
import { gqlErrorMessage } from "../../../../utils/gqlErrorCode";
import { hasRole } from "../../../../utils/permissions";
import Auth from "../../../../utils/auth";
import DoorMarginBreakdown from "./DoorMarginBreakdown";
import DoorMarginTrend from "./DoorMarginTrend";
import RecipeEditor from "./RecipeEditor";
import OperatingExpensesPanel from "./OperatingExpensesPanel";
import { money, pct, fmtDay, num, DEFAULT_DISCOUNT_PCT, discountPctValue, applyStandardDiscount, orderRefinishers } from "./doorMarginFormat";
import "../BusinessMetrics/businessMetrics.css";
import "./doorMargin.css";

/**
 * Door margin — inventory hardening Phase 4 (plans/inventory_system_hardening_2026_09_18.md
 * §4 / §6.2 / D15). ONE component, mounted twice exactly like BusinessMetricsSection:
 * the "Door Margin" tab on the Metrics page and a main tab on Business Ledger.
 *
 * Everything on screen comes from ONE stored DoorCostSnapshot (the latest, or a
 * picked one) — the web never aggregates live (D26). Recompute now runs the
 * same server code path as the Wednesday worker and the receipt-confirm trigger.
 *
 *   Recipe  = standard cost: catalog door price × pay pct, recipe materials at
 *             current prices, overhead per door
 *   Actual  = what the 90-day window actually did: realized revenue and pay per
 *             door from the same invoices payroll uses, materials calibrated by
 *             what the ledger issued
 *
 * The refinisher dropdown drives the REFINISH figures (each tech's own pay pct
 * / own doors). There is no blended "all refinishers" view (owner 2026-09-27):
 * the list is ordered by seniority and opens on the most senior refinisher.
 * Maintenance pays a flat 50% so it is one figure shown beside whoever is
 * selected. Every null renders "--".
 */
const DoorMarginSection = () => {
  const { handleSnack } = useSnackbar();
  const isAdmin = useMemo(() => {
    try {
      const { data: profile } = Auth.getProfile();
      return hasRole(profile?.permission, "admin");
    } catch {
      return false;
    }
  }, []);

  const [mode, setMode] = useState("recipe"); // recipe | actual
  const [techId, setTechId] = useState(null); // null = the most senior refinisher
  const [panel, setPanel] = useState("report"); // report | recipes | expenses
  const [pickedId, setPickedId] = useState(null);
  // Standard discount lens (Recipe mode, refinish only). On at 10% on every page
  // load by design — the typical case — then adjustable; never persisted.
  const [discountOn, setDiscountOn] = useState(true);
  const [discountRaw, setDiscountRaw] = useState(String(DEFAULT_DISCOUNT_PCT));
  const discountPct = discountOn ? discountPctValue(discountRaw) : 0;

  const { data, loading, error, refetch } = useQuery(GET_DOOR_COST_SNAPSHOT, {
    variables: { id: pickedId },
    fetchPolicy: "network-only",
  });
  const { data: listData, refetch: refetchList } = useQuery(GET_DOOR_COST_SNAPSHOTS, { fetchPolicy: "network-only" });
  const [recompute, { loading: recomputing }] = useMutation(COMPUTE_DOOR_COST_SNAPSHOT);

  const snap = data?.getDoorCostSnapshot || null;
  const history = listData?.getDoorCostSnapshots || [];

  const onRecompute = async () => {
    try {
      await recompute();
      setPickedId(null);
      await Promise.all([refetch({ id: null }), refetchList()]);
      handleSnack("Door margin recomputed", 3000);
    } catch (err) {
      handleSnack(`Recompute failed: ${gqlErrorMessage(err)}`, 5000);
    }
  };

  // Refinishers by seniority; the selection falls back to the first (most
  // senior active) whenever the picked one isn't in this snapshot.
  const techs = useMemo(() => orderRefinishers(snap?.perRefinisher, snap?.refinisherSeniority), [snap]);
  const techRow = techs.find((p) => p.userId === techId) || techs[0] || null;
  const refinish = techRow ? techRow.refinish : null;
  const rawRefinishFig = refinish ? refinish[mode] : null;
  const maintFig = snap?.maintenance ? snap.maintenance[mode] : null;
  const ovh = snap?.overheadPerDoor;
  // Per-kind overhead from the server (2026-09-27: maintenance carries no
  // marketing — return customers). Snapshots stored before that have no
  // per-kind totals; they charged every door the same sum, so fall back to it.
  const legacyOverhead = ovh
    ? (ovh.softwareCents || 0) + (ovh.marketingBlendedCents || 0) + (ovh.damageCents || 0) + (ovh.otherCents || 0)
    : null;
  const refinishOverheadCents = ovh ? ovh.refinishCents ?? legacyOverhead : null;
  const maintenanceOverheadCents = ovh ? ovh.maintenanceCents ?? legacyOverhead : null;
  const refinishFig =
    mode === "recipe"
      ? applyStandardDiscount(rawRefinishFig, {
          discountPct,
          payPct: techRow ? techRow.payPct : null,
          overheadCents: refinishOverheadCents,
        })
      : rawRefinishFig;

  const forbidden = error && /Unauthorized|Forbidden/i.test(error.message || "");

  /* `bdr-dark-placeholder` lives HERE, on the section, not on the host page.
     This section is mounted twice — Business Ledger and Metrics — and the raw
     `<input placeholder>`s in RecipeEditor were taking their readable colour
     from a wrapper only one of those hosts supplied. Owning it means the
     section reads the same wherever it is mounted. */
  return (
    <div className="dm-root bdr-dark-placeholder">
      <div className="dm-header">
        <div>
          <h2 className="dm-title">Door Margin</h2>
          <div className="dm-sub">
            {snap
              ? `Snapshot ${fmtDay(snap.computedAt)} · ${snap.trigger} · doors done ${num(snap.doorsDone?.refinish)} refinish / ${num(snap.doorsDone?.maintenance)} maintenance in the last 90 days`
              : forbidden
                ? "--"
                : "No snapshot yet"}
          </div>
        </div>
        <div className="dm-header-actions">
          {history.length > 1 && (
            <BdrSelectPopup
              id="dm-snapshot"
              className="dm-select bdr-select-trigger"
              label="Snapshot"
              value={pickedId || ""}
              onChange={(e) => setPickedId(e.target.value || null)}
              placeholder="Latest"
              options={history.map((h) => ({
                value: h.id,
                label: `${h.localDate} · ${h.trigger}`,
              }))}
            />
          )}
          {isAdmin && (
            <button type="button" className="bm-retry bdr-grad-admin-btn" disabled={recomputing} onClick={onRecompute}>
              {recomputing ? "Computing…" : "Recompute now"}
            </button>
          )}
        </div>
      </div>

      <div className="dm-tabs">
        {[
          ["report", "Report"],
          ["recipes", "Recipes"],
          ["expenses", "Operating costs"],
        ].map(([k, label]) => (
          <button
            key={k}
            type="button"
            className={`dm-pill ${panel === k ? "dm-pill-active" : ""}`}
            onClick={() => setPanel(k)}
          >
            {label}
          </button>
        ))}
      </div>

      {panel === "recipes" && <RecipeEditor isAdmin={isAdmin} />}
      {panel === "expenses" && <OperatingExpensesPanel isAdmin={isAdmin} />}

      {panel === "report" && (
        <>
          {loading && !snap && <div className="dm-empty">Loading…</div>}
          {!loading && !snap && !forbidden && (
            <div className="dm-empty">
              No snapshot yet.
              {isAdmin ? " Set up a recipe, then hit Recompute now." : " Ask an admin to recompute."}
            </div>
          )}
          {forbidden && <div className="dm-empty">--</div>}

          {snap && (
            <>
              <div className="dm-controls">
                <div className="dm-toggle" role="tablist" aria-label="Cost basis">
                  <button type="button" role="tab" aria-selected={mode === "recipe"} className={`dm-toggle-btn ${mode === "recipe" ? "dm-toggle-active" : ""}`} onClick={() => setMode("recipe")}>
                    Recipe
                  </button>
                  <button type="button" role="tab" aria-selected={mode === "actual"} className={`dm-toggle-btn ${mode === "actual" ? "dm-toggle-active" : ""}`} onClick={() => setMode("actual")}>
                    Actual
                  </button>
                </div>
                {/* Merge 2026-09-28: main DELETED the blended "All
                    refinishers" option on purpose ("no blended view") and
                    moved to `techs` (seniority-ordered) / `techRow` (defaults
                    to the most senior). Keeping our option list would have
                    resurrected a view they had just removed, so their data
                    model wins and only the control is ours. */}
                <BdrSelectPopup
                  id="dm-refinisher"
                  className="dm-select bdr-select-trigger"
                  label="Refinisher"
                  value={techRow ? techRow.userId : ""}
                  onChange={(e) => setTechId(e.target.value)}
                  placeholder="No refinishers"
                  options={techs.map((p) => ({
                    value: p.userId,
                    label: `${p.name} (${pct(p.payPct)}${
                      p.active ? "" : ", inactive"
                    })`,
                  }))}
                />
                {/* The numeric field still has not had the input pass —
                    tracked in Pass 8. */}
                {mode === "recipe" && (
                  <span className="dm-discount">
                    <BdrCheckbox
                      size="sm"
                      label="Standard discount"
                      checked={discountOn}
                      onChange={(e) => setDiscountOn(e.target.checked)}
                    />
                    <input
                      className="dm-input dm-input-num dm-discount-pct"
                      value={discountRaw}
                      disabled={!discountOn}
                      inputMode="decimal"
                      aria-label="Standard discount percent"
                      onChange={(e) => setDiscountRaw(e.target.value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1"))}
                    />
                    <span className="dm-discount-unit">% off refinish</span>
                  </span>
                )}
                <span className="dm-mode-note">
                  {mode === "recipe"
                    ? "Standard cost: catalog door price (less the standard discount when on), recipe materials at current prices."
                    : "Last 90 days: realized price and pay per door (real discounts included), materials calibrated by what was issued."}
                </span>
              </div>

              <div className="dm-kinds">
                <KindCard
                  title="Refinish"
                  subtitle={techRow ? `${techRow.name} · ${num(techRow.doors)} doors in window` : "no refinishers in this snapshot"}
                  fig={refinishFig}
                  mode={mode}
                  overheadCents={refinishOverheadCents}
                  priceLabel={mode === "recipe" && discountPct > 0 && refinishFig?.listPriceCents != null ? `Door price (catalog ${money(refinishFig.listPriceCents)} − ${num(discountPct)}%)` : null}
                  payLabel={mode === "recipe" ? `pay @ ${pct(techRow ? techRow.payPct : null)}` : "pay (realized)"}
                />
                <KindCard
                  title="Maintenance"
                  subtitle={`flat 50% pay · ${num(snap.doorsDone?.maintenance)} doors in window`}
                  fig={maintFig}
                  mode={mode}
                  overheadCents={maintenanceOverheadCents}
                  payLabel={mode === "recipe" ? "pay @ 50%" : "pay (realized)"}
                />
              </div>

              {snap.notes?.length > 0 && (
                <ul className="dm-notes">
                  {snap.notes.map((n, i) => (
                    <li key={i}>{n}</li>
                  ))}
                </ul>
              )}

              <DoorMarginBreakdown snap={snap} mode={mode} />
              <DoorMarginTrend history={history} discountPct={discountPct} techId={techRow ? techRow.userId : null} techName={techRow ? techRow.name : null} />
            </>
          )}
        </>
      )}
    </div>
  );
};

const KindCard = ({ title, subtitle, fig, mode, overheadCents, payLabel, priceLabel = null }) => {
  const price = fig ? (mode === "recipe" ? fig.doorPriceCents : fig.revenueCents) : null;
  const bp = fig ? fig.marginBp : null;
  const tone = bp == null ? "" : bp >= 2500 ? "dm-good" : bp >= 1000 ? "dm-warn-tone" : "dm-bad";
  return (
    <div className="dm-kind bm-panel bdr-grad-container-med bdr-border-thin bdr-shadow-med">
      <div className="dm-kind-head">
        <h3 className="bm-panel-title">{title}</h3>
        <span className="dm-kind-sub">{subtitle}</span>
      </div>
      <div className="dm-kind-net">
        <span className={`dm-kind-net-value ${tone}`}>{money(fig?.netCents)}</span>
        <span className="dm-kind-net-label">net per door · {bp == null ? "--" : `${(bp / 100).toFixed(1)}%`} margin</span>
      </div>
      <table className="dm-fig-table">
        <tbody>
          <tr>
            <td>{priceLabel || (mode === "recipe" ? "Door price (catalog)" : "Door price (realized)")}</td>
            <td className="bm-num">{money(price)}</td>
          </tr>
          <tr>
            <td>− {payLabel}</td>
            <td className="bm-num">{money(fig?.payCents)}</td>
          </tr>
          <tr>
            <td>− materials</td>
            <td className="bm-num">{money(fig?.materialsCents)}</td>
          </tr>
          <tr>
            <td>− overhead</td>
            <td className="bm-num">{money(overheadCents)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default DoorMarginSection;
